/*
 * Copyright (C) 2020 Reciprocity, Inc - All Rights Reserved
 * Unauthorized use, copying, distribution, displaying, or public performance
 * of this file, via any medium, is strictly prohibited. All information
 * contained herein is proprietary and confidential and may not be shared
 * with any third party without the express written consent of Reciprocity, Inc.
 */

// As we are using `graphql` through Apollo, it is not recognized as a project
// dependency this rise an error.
// eslint-disable-next-line import/no-extraneous-dependencies
import { DocumentNode } from 'graphql';

// Remove next line when we upgrade to TS 3.5
type Omit<T, K extends keyof T> = Pick<T, Exclude<keyof T, K>>;

// Keep this list ordered alphabetically
export enum ObjectType {
  Assessment = 'Assessment',
  Request = 'Request',
  Task = 'Task',
}

// TODO: define/get the validation list
export enum Validators {
  COMPLEX = 'COMPLEX',
  DECIMAL = 'DECIMAL',
  INTEGER = 'INTEGER',
  MAX_LENGTH = 'MAX_LENGTH',
  MIN_VALUE = 'MIN_VALUE',
  REQUIRED = 'REQUIRED',
  VALID_VALUES = 'VALID_VALUES',
}

interface ObjectAttributeValidation {
  validator: Validators;
  meta: any[];
  errorMessage?: string;
}

export interface ValidOption {
  value: string;
  label: string;
}

export interface RawValidOption {
  title: string;
  human: string;
}

export interface ObjectAttribute {
  name: string;
  sorCreate: boolean;
  displayName: string;
  description: string;
  placeholder: string;
  isCustomAttribute: boolean;
  validOptions: ValidOption[];
  customAttributeType: null | string;
  mandatory: boolean;
  unique: boolean;
  validation: ObjectAttributeValidation[];
}

export interface RawObjectAttribute extends Omit<ObjectAttribute, 'validOptions'> {
  validOptions: RawValidOption[];
}

export interface ZenObject {
  objectType: string;
  nameSingular: string;
  namePlural: string;
  gqlName: string;
  attrs: ObjectAttribute[];
}

export interface RawZenObject extends Omit<ZenObject, 'attrs'> {
  attrs: RawObjectAttribute[];
}

export interface AttributesGroup {
  name: string;
  label?: string;
  expanded?: boolean;
  attributes: string[];
}

export enum PresenterType {
  InputFormPresenter = 'InputFormPresenter',
  RichTextFormPresenter = 'RichTextFormPresenter',
  DropdownFormPresenter = 'DropdownFormPresenter',
  DateFormPresenter = 'DateFormPresenter',
  CheckboxFormPresenter = 'CheckboxFormPresenter',
  NumberSelectorFormPresenter = 'NumberSelectorFormPresenter',
  DaySelectorFormPresenter = 'DaySelectorFormPresenter',
  InfoMessageFormPresenter = 'InfoMessageFormPresenter',
}

export interface FieldExtraInfo {
  optionsQuery?: DocumentNode;
  dropdownOptionsParser?: (data: any) => any;
  editValueParser?: (data: any, multiple?: boolean) => any;
  submissionDataParser?: (data: any) => any;
  initialValue?: any;
}

export interface FieldInformation extends FieldRenderInformation {
  presenter: PresenterType;
}

interface GenericObject {
  [key: string]: any;
}
interface GenericFunctionObject {
  (data: any): GenericObject;
}

export interface FieldRenderInformation {
  extraInfo?: FieldExtraInfo;
  renderInfo?: {
    presenterClass?: string;
    columnClass?: string;
    customClass?: string;
    noLabel?: boolean;
    conditionalRender?: (objectContext: any) => boolean;
    presenterAttributes?: GenericObject | GenericFunctionObject;
    formGroupAttributes?: GenericObject | GenericFunctionObject;
  };
  overrideAttributes?: { [key: string]: any };
  triggerUpdates?: (formContext: any) => void;
}

export interface ObjectFormData {
  groupedAttrOrder: AttributesGroup[];
  attrMapper: Mapper;
}

export interface RenderMapper {
  [fieldName: string]: FieldRenderInformation;
}

export interface Mapper {
  [fieldName: string]: FieldInformation;
}

export interface FormField extends ObjectAttribute {
  mapper: FieldInformation;
}

export interface FieldsGroup {
  name: string;
  label: string;
  expanded?: boolean;
  groupFields: FormField[];
}

// The key is the field name, the value an object with Vuelidate validations
export interface ValidationOptions {
  [key: string]: any;
}

export interface FormData {
  [fieldName: string]: any;
}
